import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Spinner,
  Badge,
} from "reactstrap";
import { authenticatedFetch } from "../utils/apiUtils";
import ChangeEncryptionPasswordModal from "./ChangeEncryptionPasswordModal";

const EncryptionSettings = () => {
  const [currentSecurityLevel, setCurrentSecurityLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [showRecoveryKeyModal, setShowRecoveryKeyModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordModalStartInRecovery, setPasswordModalStartInRecovery] =
    useState(false);
  const [selectedSecurityLevel, setSelectedSecurityLevel] =
    useState("Standard");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [recoveryKey, setRecoveryKey] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Data reset states
  const [dataResetLoading, setDataResetLoading] = useState(false);
  const [dataResetEligible, setDataResetEligible] = useState(false);
  const [dataResetMessage, setDataResetMessage] = useState("");
  const [dataResetPasswordInput, setDataResetPasswordInput] = useState("");
  const [dataResetModalOpen, setDataResetModalOpen] = useState(false);

  const securityLevels = {
    Standard: {
      title: "Standard Security",
      description:
        "Service-recoverable encryption. Convenient and secure for most users.",
      features: [
        "AES-256 encryption",
        "Service can help with recovery",
        "No additional passwords required",
      ],
      badge: "primary",
      iterations: "10,000",
    },
    Enhanced: {
      title: "Enhanced Security",
      description:
        "Password protection with recovery key backup. Balance of security and recoverability.",
      features: [
        "AES-256 encryption",
        "Password required",
        "Recovery key backup available",
        "Higher key derivation iterations",
      ],
      badge: "warning",
      iterations: "50,000",
    },
    Maximum: {
      title: "Maximum Security",
      description:
        "Zero-knowledge encryption. Highest security, no recovery keys or service recovery.",
      features: [
        "AES-256 encryption",
        "Password required",
        "Zero-knowledge encryption",
        "No recovery keys",
        "No service recovery possible",
      ],
      badge: "danger",
      iterations: "100,000",
    },
  };

  useEffect(() => {
    fetchSecurityLevel();
    checkDataResetEligibility();
  }, []);

  const fetchSecurityLevel = async () => {
    try {
      const response = await authenticatedFetch("/encryption/security-level");

      if (response.ok) {
        const data = await response.json();
        setCurrentSecurityLevel(data.securityLevel);
      } else {
        console.error("Failed to fetch security level:", response.status);
      }
    } catch (error) {
      console.error("Error fetching security level:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMigration = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      let response;
      let payload = {};

      // Determine if this is a security level change or initial migration
      if (
        currentSecurityLevel &&
        currentSecurityLevel !== selectedSecurityLevel
      ) {
        // This is always a security level change if current level exists and is different
        payload = {
          NewSecurityLevel: selectedSecurityLevel,
          OldPassword:
            currentSecurityLevel === "Enhanced" ||
            currentSecurityLevel === "Maximum"
              ? password
              : null,
        };

        if (
          selectedSecurityLevel === "Enhanced" ||
          selectedSecurityLevel === "Maximum"
        ) {
          // When going TO Enhanced/Maximum, always need new password
          const passwordToUse =
            currentSecurityLevel === "Enhanced" ||
            currentSecurityLevel === "Maximum"
              ? newPassword
              : password;
          if (!passwordToUse) {
            setError(
              "Password is required for Enhanced and Maximum security levels"
            );
            setLoading(false);
            return;
          }
          payload.NewPassword = passwordToUse;
        }

        response = await authenticatedFetch(
          "/encryption/change-security-level",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );
      } else {
        // This is either initial setup (no current security level) or migration at same level
        payload = {
          SecurityLevel: selectedSecurityLevel,
        };

        if (
          selectedSecurityLevel === "Enhanced" ||
          selectedSecurityLevel === "Maximum"
        ) {
          if (!password) {
            setError(
              "Password is required for Enhanced and Maximum security levels"
            );
            setLoading(false);
            return;
          }
          payload.Password = password;
        }

        response = await authenticatedFetch(
          "/encryption/migrate-to-encrypted",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );
      }

      // Check if response is valid before trying to parse JSON
      if (!response) {
        throw new Error("No response received from server");
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error("Failed to parse JSON response:", jsonError);
        throw new Error("Invalid response format from server");
      }

      if (response.ok) {
        setSuccess(data.message);
        setCurrentSecurityLevel(selectedSecurityLevel);
        setShowMigrationModal(false);
        setPassword("");
        setNewPassword("");

        // Check if a recovery key was returned in the response
        if (data.recoveryKey) {
          setRecoveryKey(data.recoveryKey);
          setShowRecoveryKeyModal(true);
        }
      } else {
        setError(data.message || "Migration failed");
      }
    } catch (error) {
      setError("Migration failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateRecoveryKey = async () => {
    try {
      const response = await authenticatedFetch(
        "/encryption/generate-recovery-key",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setRecoveryKey(data.recoveryKey);
        setShowRecoveryKeyModal(true);
      } else {
        setError(data.message || "Failed to generate recovery key");
      }
    } catch (error) {
      setError("Failed to generate recovery key: " + error.message);
    }
  };

  const handleSecurityLevelChange = async (newLevel) => {
    // If we are changing security levels (and it's not the same level), we need to migrate
    if (currentSecurityLevel && newLevel !== currentSecurityLevel) {
      // Show password modal for migration
      setSelectedSecurityLevel(newLevel);
      setShowMigrationModal(true);
      return;
    }

    // For simple changes (like first-time setup when no current level exists)
    if (!currentSecurityLevel) {
      setLoading(true);
      setError("");

      try {
        const response = await authenticatedFetch(
          "/encryption/set-security-level",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ SecurityLevel: newLevel }),
          }
        );

        const data = await response.json();

        if (response.ok) {
          setCurrentSecurityLevel(newLevel);
          setSuccess(`Security level changed to ${newLevel}`);
        } else {
          setError(data.message || "Failed to change security level");
        }
      } catch (error) {
        setError("Failed to change security level: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Data reset functions
  const checkDataResetEligibility = async () => {
    setDataResetLoading(true);
    try {
      const response = await authenticatedFetch(
        "/api/DataReset/check-reset-eligibility",
        {
          method: "POST",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setDataResetEligible(data.canResetData);
        setDataResetMessage(data.message || "");
      } else {
        setDataResetEligible(false);
        setDataResetMessage("Unable to check eligibility.");
      }
    } catch (error) {
      console.error("Error checking data reset eligibility:", error);
      setDataResetEligible(false);
      setDataResetMessage("Error checking eligibility.");
    } finally {
      setDataResetLoading(false);
    }
  };

  const handleDataReset = async () => {
    if (!dataResetPasswordInput.trim()) {
      setError("Please enter your account password.");
      return;
    }

    setDataResetLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await authenticatedFetch(
        "/api/DataReset/reset-encrypted-data",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ currentPassword: dataResetPasswordInput }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuccess(
          data.message || "All encrypted data has been deleted successfully."
        );
        setDataResetModalOpen(false);
        setDataResetPasswordInput("");
        // Refresh the security level
        await fetchSecurityLevel();
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Data deletion failed.");
      }
    } catch (error) {
      console.error("Error during data reset:", error);
      setError("An error occurred during data reset.");
    } finally {
      setDataResetLoading(false);
    }
  };

  const openDataResetModal = () => {
    setDataResetModalOpen(true);
    setDataResetPasswordInput("");
    setError("");
    setSuccess("");
  };

  const openChangePasswordModal = () => {
    setPasswordModalStartInRecovery(false);
    setShowChangePasswordModal(true);
  };

  const openRecoveryModal = () => {
    setPasswordModalStartInRecovery(true);
    setShowChangePasswordModal(true);
  };

  if (loading && !currentSecurityLevel) {
    return (
      <div className="text-center p-4">
        <Spinner color="primary" />
        <p className="mt-2">Loading encryption settings...</p>
      </div>
    );
  }

  return (
    <div className="encryption-settings">
      <h4 className="mb-3">
        <i className="fas fa-shield-alt me-2"></i>
        Encryption & Security Settings
      </h4>

      {error && (
        <Alert color="danger" toggle={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" toggle={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Current Security Level */}
      <Card className="mb-4">
        <CardHeader>
          <h5 className="mb-0">
            Current Security Level:
            {currentSecurityLevel ? (
              <Badge
                color={
                  securityLevels[currentSecurityLevel]?.badge || "secondary"
                }
                className="ms-2"
              >
                {currentSecurityLevel}
              </Badge>
            ) : (
              <Badge color="warning" className="ms-2">
                Loading...
              </Badge>
            )}
          </h5>
        </CardHeader>
        <CardBody>
          {currentSecurityLevel ? (
            <>
              <p>{securityLevels[currentSecurityLevel]?.description}</p>
              <small className="text-muted">
                Key derivation iterations:{" "}
                {securityLevels[currentSecurityLevel]?.iterations}
              </small>

              {/* Change Encryption Password Button for Enhanced/Maximum */}
              {(currentSecurityLevel === "Enhanced" ||
                currentSecurityLevel === "Maximum") && (
                <div className="mt-3">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={openChangePasswordModal}
                  >
                    <i className="fas fa-key me-2"></i>
                    Change Encryption Password
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div>
              <p className="text-muted">
                Loading security level information...
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Data Loss Prevention Warning */}
      {currentSecurityLevel &&
        (currentSecurityLevel === "Enhanced" ||
          currentSecurityLevel === "Maximum") && (
          <Alert color="warning" className="mb-4">
            <h6 className="alert-heading">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Important: Data Recovery Information
            </h6>
            <hr />
            <div className="row">
              <div className="col-md-6">
                <strong>What happens if you lose access?</strong>
                <ul className="mt-2 mb-0">
                  {currentSecurityLevel === "Enhanced" && (
                    <>
                      <li>
                        <strong>Lost password:</strong> Use your recovery key to
                        change it
                      </li>
                      <li>
                        <strong>Lost recovery key:</strong> Generate a new one
                        with your password
                      </li>
                      <li>
                        <strong>Lost both:</strong> All encrypted data will be
                        permanently deleted
                      </li>
                    </>
                  )}
                  {currentSecurityLevel === "Maximum" && (
                    <>
                      <li>
                        <strong>Lost password:</strong> All encrypted data will
                        be permanently deleted
                      </li>
                      <li>
                        <strong>No recovery keys:</strong> Maximum security is
                        zero-knowledge
                      </li>
                      <li>
                        <strong>Backup externally:</strong> We cannot recover
                        your data
                      </li>
                    </>
                  )}
                </ul>
              </div>
              <div className="col-md-6">
                <strong>Prevention Tips:</strong>
                <ul className="mt-2 mb-0">
                  {currentSecurityLevel === "Enhanced" && (
                    <>
                      <li>Save your recovery key in a password manager</li>
                      <li>Print and store recovery key offline</li>
                      <li>Use a memorable but secure password</li>
                    </>
                  )}
                  {currentSecurityLevel === "Maximum" && (
                    <>
                      <li>
                        Use a password manager for your encryption password
                      </li>
                      <li>Export/backup your data regularly</li>
                      <li>Consider Enhanced security for easier recovery</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </Alert>
        )}

      {/* Security Level Options */}
      <div className="row mb-4">
        {Object.entries(securityLevels).map(([level, info]) => (
          <div key={level} className="col-md-4 mb-3">
            <Card
              className={`h-100 ${
                currentSecurityLevel === level ? "border-success" : ""
              }`}
            >
              <CardHeader>
                <h6 className="mb-0">
                  <Badge color={info.badge}>{info.title}</Badge>
                </h6>
              </CardHeader>
              <CardBody>
                <p className="small">{info.description}</p>
                <ul className="small">
                  {info.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                {currentSecurityLevel !== level && (
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => handleSecurityLevelChange(level)}
                    disabled={loading}
                  >
                    Switch to {level}
                  </Button>
                )}
                {currentSecurityLevel === level && (
                  <Badge color="success">Current Level</Badge>
                )}
              </CardBody>
            </Card>
          </div>
        ))}
      </div>

      {/* Migration Section */}
      <Card>
        <CardHeader>
          <h5 className="mb-0">
            <i className="fas fa-database me-2"></i>
            Data Migration
          </h5>
        </CardHeader>
        <CardBody>
          <p>
            Migrate your existing unencrypted items to encrypted storage. This process provides enhanced security for your data.
          </p>
          <Button
            color="primary"
            onClick={() => setShowMigrationModal(true)}
            disabled={loading}
          >
            <i className="fas fa-lock me-2"></i>
            Migrate Items to Encrypted Storage
          </Button>
        </CardBody>
      </Card>

      {/* Password Management Section */}
      <Card>
        <CardHeader>
          <h5 className="mb-0">
            <i className="fas fa-key me-2"></i>
            Password Management
          </h5>
        </CardHeader>
        <CardBody>
          <p>
            Need to reset your password or recover access to encrypted data?
          </p>
          <Button color="outline-primary" size="sm" onClick={openRecoveryModal}>
            <i className="fas fa-shield-alt me-2"></i>
            Password Reset & Recovery
          </Button>
          <FormText color="muted" className="mt-2">
            Password reset impact varies by security level. Enhanced users need
            recovery keys, Maximum users will lose all encrypted data.
          </FormText>
        </CardBody>
      </Card>

      {/* Migration Modal */}
      <Modal
        isOpen={showMigrationModal}
        toggle={() => setShowMigrationModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowMigrationModal(false)}>
          Migrate to Encrypted Storage
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="securityLevel">Choose Security Level</Label>
              <Input
                type="select"
                id="securityLevel"
                value={selectedSecurityLevel}
                onChange={(e) => setSelectedSecurityLevel(e.target.value)}
              >
                {Object.entries(securityLevels).map(([level, info]) => (
                  <option key={level} value={level}>
                    {info.title}
                  </option>
                ))}
              </Input>
              <FormText color="muted">
                {securityLevels[selectedSecurityLevel]?.description}
              </FormText>
            </FormGroup>

            {/* Show current password field if changing from Enhanced/Maximum */}
            {currentSecurityLevel &&
              (currentSecurityLevel === "Enhanced" ||
                currentSecurityLevel === "Maximum") && (
                <FormGroup>
                  <Label for="currentPassword">Current Password</Label>
                  <Input
                    type="password"
                    id="currentPassword"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your current encryption password"
                    required
                  />
                  <FormText color="muted">
                    Required to decrypt your existing{" "}
                    {currentSecurityLevel.toLowerCase()}-encrypted data.
                  </FormText>
                </FormGroup>
              )}

            {/* Show new password field if going to Enhanced/Maximum */}
            {(selectedSecurityLevel === "Enhanced" ||
              selectedSecurityLevel === "Maximum") && (
              <FormGroup>
                <Label for="newPassword">
                  {currentSecurityLevel &&
                  (currentSecurityLevel === "Enhanced" ||
                    currentSecurityLevel === "Maximum")
                    ? "New Password"
                    : "Encryption Password"}
                </Label>
                <Input
                  type="password"
                  id="newPassword"
                  value={
                    currentSecurityLevel &&
                    (currentSecurityLevel === "Enhanced" ||
                      currentSecurityLevel === "Maximum")
                      ? newPassword
                      : password
                  }
                  onChange={(e) => {
                    if (
                      currentSecurityLevel &&
                      (currentSecurityLevel === "Enhanced" ||
                        currentSecurityLevel === "Maximum")
                    ) {
                      setNewPassword(e.target.value);
                    } else {
                      setPassword(e.target.value);
                    }
                  }}
                  placeholder={
                    currentSecurityLevel &&
                    (currentSecurityLevel === "Enhanced" ||
                      currentSecurityLevel === "Maximum")
                      ? "Enter a new encryption password"
                      : "Enter a strong password for encryption"
                  }
                  required
                />
                <FormText color="muted">
                  This password will be required to access your encrypted data.
                  {selectedSecurityLevel === "Maximum" &&
                    " Store it securely - it cannot be recovered!"}
                </FormText>
              </FormGroup>
            )}
          </Form>

          {/* Special warning for Maximum security level */}
          {selectedSecurityLevel === "Maximum" && (
            <Alert color="warning">
              <strong>⚠️ Maximum Security Warning</strong>
              <ul className="mb-0 mt-2">
                <li>
                  <strong>Zero-Knowledge:</strong> Only your password can
                  decrypt your data
                </li>
                <li>
                  <strong>No Recovery Keys:</strong> We cannot generate recovery
                  keys for Maximum security
                </li>
                <li>
                  <strong>Password Loss = Data Loss:</strong> If you forget your
                  password, your data will be permanently inaccessible
                </li>
                <li>
                  <strong>Make sure to store your password securely!</strong>
                </li>
              </ul>
            </Alert>
          )}

          <Alert color="info">
            <strong>What happens during migration?</strong>
            <ul className="mb-0 mt-2">
              <li>All your items will be encrypted using AES-256 encryption</li>
              <li>
                Original data is securely replaced with encrypted versions
              </li>
              <li>You can continue using the app normally</li>
              <li>Enhanced security protection for your sensitive data</li>
            </ul>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setShowMigrationModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleMigration}
            disabled={
              loading ||
              // If changing from Enhanced/Maximum, current password is required
              (currentSecurityLevel &&
                (currentSecurityLevel === "Enhanced" ||
                  currentSecurityLevel === "Maximum") &&
                !password) ||
              // If going to Enhanced/Maximum and we're already Enhanced/Maximum, new password is required
              ((selectedSecurityLevel === "Enhanced" ||
                selectedSecurityLevel === "Maximum") &&
                currentSecurityLevel &&
                (currentSecurityLevel === "Enhanced" ||
                  currentSecurityLevel === "Maximum") &&
                !newPassword) ||
              // If going to Enhanced/Maximum from Standard, password is required
              ((selectedSecurityLevel === "Enhanced" ||
                selectedSecurityLevel === "Maximum") &&
                (!currentSecurityLevel ||
                  currentSecurityLevel === "Standard") &&
                !password)
            }
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Migrating...
              </>
            ) : (
              <>
                <i className="fas fa-lock me-2"></i>
                Start Migration
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Recovery Key Modal */}
      <Modal
        isOpen={showRecoveryKeyModal}
        toggle={() => setShowRecoveryKeyModal(false)}
        size="lg"
      >
        <ModalHeader toggle={() => setShowRecoveryKeyModal(false)}>
          <i className="fas fa-key me-2"></i>
          Your Recovery Key
        </ModalHeader>
        <ModalBody>
          <Alert color="warning">
            <strong>Important!</strong> Save this recovery key in a secure
            location. You will need it to access your encrypted data if you
            forget your password.
          </Alert>

          <div className="bg-light p-3 rounded border">
            <code style={{ fontSize: "14px", wordBreak: "break-all" }}>
              {recoveryKey}
            </code>
          </div>

          <div className="mt-3">
            <Button
              color="primary"
              outline
              size="sm"
              onClick={() => navigator.clipboard.writeText(recoveryKey)}
            >
              <i className="fas fa-copy me-2"></i>
              Copy to Clipboard
            </Button>
          </div>

          <Alert color="info" className="mt-3">
            <strong>Recovery Key Tips:</strong>
            <ul className="mb-0 mt-2">
              <li>Store in a password manager</li>
              <li>Write it down and keep in a secure location</li>
              <li>Don't share it with anyone</li>
              <li>This key cannot be regenerated</li>
            </ul>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={() => setShowRecoveryKeyModal(false)}
          >
            <i className="fas fa-check me-2"></i>
            I've Saved My Recovery Key
          </Button>
        </ModalFooter>
      </Modal>

      {/* Change Encryption Password Modal */}
      <ChangeEncryptionPasswordModal
        show={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        startInRecoveryMode={passwordModalStartInRecovery}
      />

      {/* Data Reset Section */}
      {dataResetEligible && (
        <Card className="mt-4 border-danger">
          <CardHeader className="bg-danger text-white">
            <h5 className="mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Nuclear Option: Delete All Encrypted Data
            </h5>
          </CardHeader>
          <CardBody>
            <p className="text-muted">
              {dataResetMessage ||
                "If you've lost both your encryption password AND recovery key, your only option is to delete all encrypted data."}
            </p>

            <Alert color="info" className="mb-3">
              <strong>⚠️ Try These First:</strong>
              <ul className="mb-0 mt-2">
                <li>
                  <strong>Enhanced Users:</strong> Use "Password Reset &
                  Recovery" with your recovery key
                </li>
                <li>
                  <strong>Maximum Users:</strong> Double-check you have the
                  correct encryption password
                </li>
                <li>
                  <strong>All Users:</strong> Check password managers, notes, or
                  other secure storage
                </li>
              </ul>
            </Alert>

            <div className="d-grid">
              <Button
                color="danger"
                onClick={openDataResetModal}
                disabled={dataResetLoading}
              >
                <i className="fas fa-trash me-2"></i>
                Delete All Encrypted Data
              </Button>
            </div>

            <small className="text-muted mt-2 d-block">
              This option requires your account password for security
              verification.
              <strong> This action cannot be undone!</strong>
            </small>
          </CardBody>
        </Card>
      )}

      {/* Data Reset Modal */}
      <Modal
        isOpen={dataResetModalOpen}
        toggle={() => setDataResetModalOpen(false)}
      >
        <ModalHeader toggle={() => setDataResetModalOpen(false)}>
          Delete All Encrypted Data
        </ModalHeader>
        <ModalBody>
          <Alert color="danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>This action cannot be undone!</strong>
          </Alert>

          <div>
            <p>
              <strong>This will permanently delete:</strong>
            </p>
            <ul>
              <li>All your encrypted items and data</li>
              <li>All pending transfers</li>
              <li>Your recovery keys</li>
              <li>Your current security level will be reset to Standard</li>
            </ul>
          </div>

          <Alert color="info" className="mt-3">
            <strong>After deletion:</strong>
            <ul className="mb-0 mt-2">
              <li>
                Your account will use Standard security (service-recoverable)
              </li>
              <li>You can upgrade to Enhanced/Maximum security again</li>
              <li>You'll need to re-add all your assets manually</li>
            </ul>
          </Alert>

          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleDataReset();
            }}
          >
            <FormGroup>
              <Label for="dataResetPassword">Account Password</Label>
              <Input
                type="password"
                id="dataResetPassword"
                value={dataResetPasswordInput}
                onChange={(e) => setDataResetPasswordInput(e.target.value)}
                placeholder="Enter your account password"
                required
                disabled={dataResetLoading}
              />
              <FormText color="muted">
                Your account password is required for security verification.
              </FormText>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setDataResetModalOpen(false)}
            disabled={dataResetLoading}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleDataReset}
            disabled={dataResetLoading || !dataResetPasswordInput.trim()}
          >
            {dataResetLoading && <Spinner size="sm" className="me-2" />}
            Delete All Data
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default EncryptionSettings;
